import { useCallback, useMemo, useRef, useState } from 'react';

import { AlertDialog, Button } from '@fhs/ui';
import { getPromiseWithResolvers } from '@fhs/utils';

type ResetCustomizationsAlertActions = 'accepted' | 'cancelled';

export function useResetCustomizationsAlert() {
  const pRef = useRef(getPromiseWithResolvers<ResetCustomizationsAlertActions>());
  const [isVisible, setIsVisible] = useState(false);

  const resetPromise = useCallback(() => {
    pRef.current = getPromiseWithResolvers<ResetCustomizationsAlertActions>();
  }, []);

  const handleAccept = useCallback(() => {
    pRef.current.resolve('accepted');
    resetPromise();
    setIsVisible(false);
  }, [resetPromise]);

  const handleCancel = useCallback(() => {
    pRef.current.resolve('cancelled');
    resetPromise();
    setIsVisible(false);
  }, [resetPromise]);

  const handleOpen = useCallback(() => {
    setIsVisible(true);

    return pRef.current.promise;
  }, []);

  const alertDialog = useMemo(
    () => (
      <AlertDialog
        onClose={handleCancel}
        visible={isVisible}
        headingContent="Reset Customizations"
        bodyContent="Selecting this option will clear any customization applied to this item. Would you like to continue?"
        actions={
          <>
            <Button size="lg" type="solid" onPress={handleAccept}>
              <Button.Text>Continue</Button.Text>
            </Button>
            <Button size="lg" type="outline" onPress={handleCancel}>
              <Button.Text>Cancel</Button.Text>
            </Button>
          </>
        }
      />
    ),
    [handleCancel, handleAccept, isVisible]
  );

  return useMemo(
    () => ({
      resetCustomizationsAlertDialog: alertDialog,
      openResetCustomizationsAlertDialog: handleOpen,
    }),
    [alertDialog, handleOpen]
  );
}
